import dayjs, { Dayjs } from 'dayjs';

export const DASHBOARD_PERIODS = ['week', 'month', 'threeMonths', 'sixMonths', 'future'] as const;
export type DashboardPeriod = (typeof DASHBOARD_PERIODS)[number];

export interface PeriodRange {
  from: Dayjs;
  to: Dayjs;
  previousFrom: Dayjs;
  previousTo: Dayjs;
  isFuture: boolean;
}

const periodDuration = (
  period: DashboardPeriod
): { amount: number; unit: dayjs.ManipulateType } => {
  switch (period) {
    case 'week':
      return { amount: 7, unit: 'day' };
    case 'month':
      return { amount: 1, unit: 'month' };
    case 'threeMonths':
      return { amount: 3, unit: 'month' };
    case 'sixMonths':
    case 'future':
      return { amount: 6, unit: 'month' };
  }
};

export const getPeriodRange = (period: DashboardPeriod, now: Dayjs = dayjs()): PeriodRange => {
  const { amount, unit } = periodDuration(period);

  if (period === 'future') {
    const from = now.startOf('day');
    const to = now.add(amount, unit).endOf('day');
    return { from, to, previousFrom: from, previousTo: to, isFuture: true };
  }

  const to = now.endOf('day');
  const from = now.subtract(amount, unit).startOf('day');
  return {
    from,
    to,
    previousTo: from,
    previousFrom: from.subtract(amount, unit),
    isFuture: false,
  };
};

export type ChangeDirection = 'up' | 'down' | 'flat';

export interface PercentChange {
  /** Signed ratio, e.g. 0.25 = +25%. `null` when there is no comparable previous value. */
  ratio: number | null;
  direction: ChangeDirection;
}

export const computePercentChange = (current: number, previous: number): PercentChange => {
  if (previous === 0) {
    if (current === 0) return { ratio: 0, direction: 'flat' };
    return { ratio: null, direction: 'up' };
  }
  const ratio = (current - previous) / previous;
  return { ratio, direction: ratio > 0 ? 'up' : ratio < 0 ? 'down' : 'flat' };
};
